import { ChevronDownIcon, CircleInfoIcon } from "@xola/icons";
import { Alert, almostZero, Currency, FormGroup, Label, PopoverList } from "@xola/ui-kit";
import { getPaymentLineItems, isStoreCredit } from "common";
import { isUndefined, startCase } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { PaymentMethod, PaymentMethodType, Purchase, RemoteCard } from "types";
import { useCollection } from "../../hooks/useCollection";
import { useVisibility } from "../../hooks/useVisibility";
import { NoTranslate } from "../Common/NoTranslate";
import { SecondaryButton } from "../Common/SecondaryButton";
import { FilterMenuButton } from "../Dashboard/Filters/FilterMenuButton";
import { Loader } from "../Loader";
import { CardSelectionMode } from "./Refund/helpers";
import { useSeller } from "@/hooks/useAccount";
import { isPurchaseWithInteracCard } from "@/models/Purchase";

interface SavedCardsProps {
    showAutomaticSplitRefund?: boolean;
    purchase: Purchase;
    className: string;
    onChange: (card: RemoteCard | null) => void;
}

export const SavedCards = ({ showAutomaticSplitRefund = false, purchase, className, onChange }: SavedCardsProps) => {
    const { data: savedCards, isLoading } = useCollection<RemoteCard>(["purchases", purchase.id, "remoteCards"], null, {
        staleTime: 1000, // Cache for 1s so that its always re-fetched when the page loads
        keepPreviousData: true,
    });

    // Setting it as null by default, since when a purchase A is selected it might have selected card C1,
    // when any other purchase is selected, we are still displaying card C1 in the list even thought its not
    // part of the purchase. because the savedCards data might not be updated by then.
    const [selectedCard, setSelectedCard] = useState<RemoteCard | null>(null);

    const updateSavedCard = useCallback(
        (card: RemoteCard | null) => {
            setSelectedCard(card);
            onChange(card);
        },
        [onChange],
    );

    const ccPaymentLineItems = getPaymentLineItems(purchase).filter(
        (lineItem) =>
            lineItem.payment?.method === PaymentMethod.CREDIT_CARD &&
            !lineItem.fundingSource?.id &&
            !lineItem.refundToStoreCredit,
    );
    const isCcPaymentWithoutCard =
        ccPaymentLineItems.length > 0 && !isStoreCredit(purchase)
            ? ccPaymentLineItems.some((lineItem) => !lineItem.payment?.card)
            : false;

    const hasAnyInteracPayment = isPurchaseWithInteracCard(purchase);

    const showAutomaticSplitRefundOption =
        showAutomaticSplitRefund && savedCards && savedCards.length > 1 && !hasAnyInteracPayment;
    const emvPaymentWithoutCard = !hasAnyInteracPayment && isCcPaymentWithoutCard && savedCards.length === 1;
    const isUnsetSelectedCard = showAutomaticSplitRefundOption || emvPaymentWithoutCard;

    useEffect(() => {
        if (savedCards) {
            if (isUnsetSelectedCard) {
                updateSavedCard(null);
            } else {
                const cardWithoutRefund = savedCards?.find((card) => !isCardFullyRefunded(card.id, savedCards));
                if (cardWithoutRefund) {
                    updateSavedCard(cardWithoutRefund);
                }
            }
        }
    }, [updateSavedCard, savedCards, isUnsetSelectedCard]);

    // Unsetting the card when component is unmounted.
    useEffect(() => {
        return () => {
            setSelectedCard(null);
        };
    }, []);

    const handleChangeSelected = (e: any, name: string, id: ID | null) => {
        if (id === null && name === CardSelectionMode.AUTOMATIC_CARD_SELECTION) {
            updateSavedCard(null);
            return;
        }

        // Leaving old matching method as a fallback for now
        const matchingCard = id
            ? savedCards?.find((savedCard) => savedCard.id === id)
            : savedCards.find((savedCard) => new RegExp(savedCard.last4).exec(name));
        if (matchingCard) {
            updateSavedCard(matchingCard);
        }
    };

    if (!isLoading && emvPaymentWithoutCard) {
        return null;
    }

    return (
        <div data-testid="saved-cards" className={className}>
            <Label>Credit Card</Label>
            {isLoading ? (
                <Loader />
            ) : savedCards ? (
                <SavedCardMenu
                    expanded
                    showAutomaticSplitRefund={showAutomaticSplitRefundOption}
                    savedCards={savedCards}
                    selectedSavedCard={selectedCard}
                    onChangeSelected={handleChangeSelected}
                />
            ) : null}
        </div>
    );
};

interface SavedCardMenuProps {
    showAutomaticSplitRefund?: boolean;
    savedCards?: RemoteCard[];
    selectedSavedCard?: RemoteCard | null;
    onChangeSelected: (e: any, name: string, id: ID | null) => void;
    expanded: boolean;
}

export const SavedCardMenu = ({
    showAutomaticSplitRefund = false,
    savedCards,
    selectedSavedCard,
    onChangeSelected,
    expanded,
}: SavedCardMenuProps) => {
    const [visible, { toggle, hide }] = useVisibility(false);
    const { currency } = useSeller();

    const handleChangeSelected = (e: any, name: string, id: string | null) => {
        if (savedCards && isCardFullyRefunded(id, savedCards)) {
            return;
        }

        onChangeSelected(e, name, id);
        hide();
    };

    return expanded ? (
        <FormGroup>
            <PopoverList
                distance={18}
                visible={visible}
                className="no-translate max-h-50 !w-96 !max-w-full overflow-y-auto"
                onClickOutside={hide}
            >
                <FilterMenuButton
                    isDropdown
                    text={
                        selectedSavedCard
                            ? getSavedCardText(selectedSavedCard)
                            : showAutomaticSplitRefund
                            ? CardSelectionMode.AUTOMATIC_CARD_SELECTION
                            : ""
                    }
                    className="no-translate !w-[400px] !max-w-full"
                    data-testid="saved-card-menu-filter-menu-button"
                    onClick={toggle}
                />
                {showAutomaticSplitRefund && (
                    <PopoverList.Item
                        name={CardSelectionMode.AUTOMATIC_CARD_SELECTION}
                        isActive={selectedSavedCard === null}
                        onClickItem={handleChangeSelected}
                    >
                        {" "}
                        <NoTranslate>{CardSelectionMode.AUTOMATIC_CARD_SELECTION}</NoTranslate>
                    </PopoverList.Item>
                )}
                {savedCards?.map((savedCard: RemoteCard) => (
                    <PopoverList.Item
                        key={savedCard.id}
                        id={savedCard.id}
                        name={getSavedCardText(savedCard)}
                        isActive={savedCard.id === selectedSavedCard?.id}
                        data-testid="saved-card-menu-popover-list-item"
                        className={isCardFullyRefunded(savedCard.id, savedCards) ? "opacity-50" : ""}
                        onClickItem={handleChangeSelected}
                    >
                        <NoTranslate>{getSavedCardText(savedCard)}</NoTranslate>
                        {!isUndefined(savedCard.value) && (
                            <div className="pl-20">
                                <Currency currency={currency}>{savedCard.value}</Currency>
                            </div>
                        )}
                    </PopoverList.Item>
                ))}
            </PopoverList>
        </FormGroup>
    ) : (
        <PopoverList
            distance={1}
            trigger="click"
            placement="bottom"
            className="no-translate max-h-36 !w-[400px] !max-w-[400px] -translate-x-[182px] overflow-y-auto !py-0 font-normal"
        >
            <SecondaryButton size="tiny" className="rounded-l-none border-l-transparent py-3">
                <ChevronDownIcon />
            </SecondaryButton>

            {showAutomaticSplitRefund && (
                <PopoverList.Item
                    name={CardSelectionMode.AUTOMATIC_CARD_SELECTION}
                    isActive={selectedSavedCard === null}
                    onClickItem={handleChangeSelected}
                >
                    <NoTranslate>{CardSelectionMode.AUTOMATIC_CARD_SELECTION}</NoTranslate>
                </PopoverList.Item>
            )}
            {savedCards?.map((savedCard: RemoteCard) => (
                <PopoverList.Item
                    key={savedCard.id}
                    id={savedCard.id}
                    name={getSavedCardText(savedCard)}
                    isActive={savedCard.id === selectedSavedCard?.id}
                    onClickItem={handleChangeSelected}
                >
                    <NoTranslate>{getSavedCardText(savedCard)}</NoTranslate>
                </PopoverList.Item>
            ))}
        </PopoverList>
    );
};

const getSavedCardText = (savedCard: RemoteCard) => {
    return `${startCase(savedCard.brand)} **** **** **** ${savedCard.last4}`;
};

interface InteracPaymentMessageProps {
    isEMVConnected?: boolean;
    message?: string;
}

export const InteracPaymentMessage = ({ isEMVConnected = false, message }: InteracPaymentMessageProps) => {
    return (
        <Alert color={isEMVConnected ? "primary" : "danger"}>
            <div className="flex">
                <div className="self-center">
                    <CircleInfoIcon size="large" />
                </div>
                <div className="ml-2">
                    <span className="align-middle text-base">
                        {isEMVConnected ? (
                            message ?? "The customer must present the physical card in order to process this refund."
                        ) : (
                            <>
                                <strong>No EMV device was detected.</strong> The customer must present the physical card
                                in order to process this refund. Please connect your EMV device to proceed with this
                                refund.
                            </>
                        )}
                    </span>
                </div>
            </div>
        </Alert>
    );
};

const isCardFullyRefunded = (savedCardId: ID | null, savedCards: RemoteCard[]) => {
    const savedCard = savedCards.find((savedCard) => savedCard.id === savedCardId);

    if (savedCard?.paymentMethodType !== PaymentMethodType.INTERAC) {
        return false;
    }

    return savedCard?.value !== undefined && almostZero(savedCard?.value);
};
